import { useMemo, useState } from "react";
import { Play } from "lucide-react";

import AttachmentPreviewModal from "../messaging/AttachmentPreviewModal";
import { normalizeReviewMedia, toReviewAttachmentMeta } from "../../utils/reviewMedia";

export default function ReviewMediaGallery({
  media = [],
  legacyPhotos = [],
  className = "",
  itemClassName = "",
}) {
  const normalizedMedia = useMemo(
    () => normalizeReviewMedia(media, legacyPhotos),
    [legacyPhotos, media]
  );
  const [activeIndex, setActiveIndex] = useState(null);

  if (normalizedMedia.length === 0) return null;

  const activeMedia =
    activeIndex != null && normalizedMedia[activeIndex]
      ? toReviewAttachmentMeta(normalizedMedia[activeIndex], activeIndex)
      : null;

  return (
    <>
      <div className={`flex flex-wrap gap-2.5 ${className}`.trim()}>
        {normalizedMedia.map((item, index) => {
          const isVideo = item.mediaType === "video";

          return (
            <button
              key={`${item.url}-${index}`}
              type="button"
              onClick={() => setActiveIndex(index)}
              className={`group relative h-[72px] w-[72px] overflow-hidden rounded-2xl border border-neutral-200/80 bg-gradient-to-br from-white to-neutral-100 shadow-sm transition duration-200 hover:-translate-y-0.5 hover:border-neutral-300 hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 dark:border-neutral-700 dark:from-neutral-900 dark:to-neutral-800 dark:hover:border-neutral-600 dark:hover:shadow-none dark:focus-visible:ring-offset-neutral-900 sm:h-20 sm:w-20 ${itemClassName}`.trim()}
              aria-label={isVideo ? `Preview review video ${index + 1}` : `Preview review image ${index + 1}`}
            >
              <span className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-black/5 dark:ring-white/5" />
              {isVideo ? (
                <>
                  <video
                    src={item.url}
                    className="h-full w-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                  <span className="pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t from-black/55 via-black/15 to-transparent" />
                  <span className="absolute left-2 top-2 inline-flex items-center rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                    Video
                  </span>
                  <span className="absolute inset-0 flex items-center justify-center text-white">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-black/55 shadow-sm transition group-hover:scale-105">
                      <Play size={14} fill="currentColor" />
                    </span>
                  </span>
                </>
              ) : (
                <img
                  src={item.url}
                  alt={`Review media ${index + 1}`}
                  className="h-full w-full object-cover"
                  loading="lazy"
                />
              )}
            </button>
          );
        })}
      </div>

      {activeMedia ? (
        <AttachmentPreviewModal
          attachment={activeMedia}
          onClose={() => setActiveIndex(null)}
        />
      ) : null}
    </>
  );
}
